<template>
    <div class="table-container relative" style=" overflow-x: auto; min-height: 450px;" >
        <jet-input
            v-if="!hideSearchFilter"
            class="mt-1 block w-64 absolute top-0 right-0"
            v-model="search"
            :placeholder="searchPlaceholder ? searchPlaceholder : 'Search'"
        />
        <div class="table-header flex justify-between items-center"
        :class="{ 'mb-2': filters && filters.length > 0, 'mb-12': !filters || filters.length == 0}"
        >
            <template v-if="filters && filters.length > 0">
                <filter-panel/>
            </template>
            <template v-else>
                <div class="mt-4"></div>
            </template>
        </div>
        <select-bar v-if="selected.length"/>
    
        <div class="grid-container border border-gray-200 " v-if="columns" 
            style="min-width: 1000px;"
            :style="{
                gridTemplateColumns: computedColumns.map(column => column.width ? column.width : '1fr').join(' ')
            }"
        >
            <div class="column-header bg-gray-100 border-b border-gray-300 text-gray-700 font-bold capitalize pl-4 flex items-center"
                v-for="column in computedColumns"
                :key="column.name"
                @click="toggleSort(column)"
                :class="{ 'cursor-pointer': column.sortable }"
            >
                {{ column.label }}
                <div v-if="column.sortable" class="ml-2 flex flex-col" style="font-size: 8px;">
                    <font-awesome-icon :icon="['fas', 'arrow-up']" style="transform: translateY(-1px)"
                        :class="{ 
                            'text-gray-800': orderBy == column.name && orderByDir == 'asc',
                            'text-gray-500' : orderBy == column.name && orderByDir != 'asc'
                        }"
                    ></font-awesome-icon>
                    <font-awesome-icon :icon="['fas', 'arrow-down']" style="transform: translateY(1px)"
                        :class="{ 'text-gray-800': orderBy == column.name && orderByDir == 'desc',
                            'text-gray-500' : orderBy == column.name && orderByDir != 'desc'
                    }"
                    ></font-awesome-icon>
                </div>
            </div>
            <div v-if="loading" class="flex justify-center items-center" style="grid-column: span 100; height: 490px;">
                <font-awesome-icon icon="spinner" class="fa-spin text-2xl" />
            </div>

            <template v-if="!loading">
                <template v-for="(item, index) in items">
                    <!-- Loop through each column for the current item -->
                    <div v-for="(column, index) in computedColumns" :key="column.name + '_' + (item.tra_id ? item.tra_id : item.id)" 
                        class='row flex items-center border-b border-gray-200 text-absolute-black' :class="combinedColumnClass(item, column, index)" :style="column.style"
                        @mouseover="hoverRow(item.tra_id ? item.tra_id : item.id)"
                        @mouseout="unhoverRow(item.tra_id ? item.tra_id : item.id)"
                        @click="handleClickable(item)"
                    >
                        <!-- Check if a component is specified and render it -->
                        <component 
                            v-if="column.component" :is="column.component" :data="item"
                            @event="column.eventHandler(item, $event)"
                            :value="isSelected(item)"
                            @toggle="toggleSelected($event, item)"
                        ></component>
                        <!-- Fallback if no component is specified, just display the data -->
                        <div v-else>
                            {{ getItemValue(item, column.name) }}
                        </div>
                    </div>
                </template>
            </template>
            
        </div>
        <pagination-row v-if="!disablePagination" />
    </div>
</template>


<script>
import axios from 'axios';
import paginationRow from './PaginationRow.vue';
import {mapWritableState, mapActions} from 'pinia';
import { useGeneralTableStore } from '@/Stores/general_table';
import SelectableInput from './TableComponents/SelectableInput.vue';
import SelectBar from './TableComponents/SelectBar.vue';
import jetInput from '@/Jetstream/Input.vue';
import filterPanel from './TableComponents/filterPanel.vue';
import {markRaw} from 'vue';

import _ from 'lodash';

export default {
    props: {
        columns: {
            type: Array,
            default: () => []
        },
        filters: {
            type: Array,
            default: () => []
        },
        selectActions: {
            type: Array,
            default: () => []
        },
        url: {
            type: String,
            default: ''
        },
        disablePagination: {
            type: Boolean,
            default: false
        },
        searchPlaceholder: {
            type: String,
            default: ''
        },
        clickableRow: {
            type: Object,
            default: () => ({})
        },
        hideSearchFilter: {
            type: Boolean,
            default: false
        },
        selectable: {
            type: Boolean,
            default: false
        },
        itemNoun: {
            type: String,
            default: 'item'
        },
    },

    components: {
        paginationRow, jetInput, filterPanel, SelectBar
    },
    computed: {
        ...mapWritableState(useGeneralTableStore, [
            'data',
            'page',
            'fetchPage',
            'perPage',
            'search',
            'orderBy',
            'orderByDir',
            'loading',
            'items',
            'availableFilters',
            'activeFilters',
            'filterOpen',
            'combinedUrlParams',
            'applyFilterByValue',
            'hovering',
            'selected',
        ]),
        ...mapWritableState(useGeneralTableStore, {
            storeURL: 'url',
            storeItemNoun: 'itemNoun',
            storeSelectActions: 'selectActions',
        }),
        computedColumns() {
            //if selectable, we'll have to include a column for the checkbox
            let checkboxColumn = {
                label: '',
                name: 'selectable',
                sortable: false,
                width: '55px',
                component: markRaw(SelectableInput),
            };

            return this.selectable ? [checkboxColumn, ...this.columns] : this.columns;
        },
        isClickable() {
            if (Object.keys(this.clickableRow).length == 0) {
                return false;
            }
            if (this.clickableRow.enabled === false) {
                return false;
            }
            return true;
        }
    },
    data: () => ({
        initializing: true, //used for 'search' watcher
    }),
    async mounted() {
        this.loading = true;
        this.initializing = true;
        this.storeItemNoun = this.itemNoun;
        this.storeSelectActions = this.selectActions;
        // Create URLSearchParams object from the current URL
        const searchParams = new URLSearchParams(window.location.search);
        // Prepare an object to hold any filter parameters for later use
        this.clearData({clearSearch: true}); 
        this.availableFilters = this.filters;
        this.activeFilters = [];


        let imported = [];
        // Loop through all parameters
        for (const [key, value] of searchParams) {
            if (key === 'page') {
                this.fetchPage = parseInt(value);
            } else if (key === 'per_page') {
                this.perPage = parseInt(value);
            } else if (key === 'order_by') {
                this.orderBy = value;
            } else if (key === 'order_by_dir') {
                this.orderByDir = value;
            } else if (key == 'contact[]') {
                //this is a special case for contacts, because multiple may come in, however, we only apply one filter
                await this.applyFilterByValue('contact', searchParams.getAll('contact[]'));
            } else if (key == 'search') {
                this.search = value;
            } else {
                let isArray = false;
                let updatedKey = key;
                //check if name ends with '[]', if so, we know it's an array
                if (key.slice(-2) == '[]') {
                    isArray = true;
                    updatedKey = key.slice(0, -2);
                }
                let filter = this.availableFilters.find(f => f.value == updatedKey);
                if (
                    (filter.multiple && isArray) || 
                    (!filter.multiple && !isArray)
                ) {
                    //each value will be a separate filter
                    await this.applyFilterByValue(updatedKey, value);
                } else {
                    if (imported.includes(updatedKey)) continue;
                    //this is a single filter with an array of values
                    await this.applyFilterByValue(updatedKey, searchParams.getAll(updatedKey));
                    imported.push(updatedKey);
                }
            }
        }
        this.initializing = false;
        this.loading = false;
    },
    methods: {
        ...mapActions(useGeneralTableStore, [
            'fetchData', 'clearData'
        ]),
        getItemValue(item, prop) {
            return _.get(item, prop);
        },
        isSelected(item) {
            //does item have a 'tra_id'? if so, use it, otherwise, use 'id'
            let id = item.tra_id ? 'tra_id' : 'id';
            let found = this.selected.find(s => s[id] == item[id]);
            return found ? true : false;
        },
        toggleSelected(shouldBeSelected, item) {
            //does item have a 'tra_id'? if so, use it, otherwise, use 'id'
            let id = item.tra_id ? 'tra_id' : 'id';
            let found = this.selected.find(s => s[id] == item[id]);

            if (found && !shouldBeSelected) {
                this.selected = this.selected.filter(s => s[id] != item[id]);
            } else if (!found && shouldBeSelected) {
                this.selected.push(item);
            }
        },
        debouncedSearch: _.debounce(function() {
            this.clearData();
            this.fetchPage = 1;
            this.fetchData();
        }, 500),
        handleClickable(item) {
            if (!this.isClickable) return;

            this.$inertia.visit(route(this.clickableRow.route, item[this.clickableRow.id]));
        },
        hoverRow(id) {
            //we are actually only going to do this if there is an action on click for the row
            if (!this.isClickable) return;
            this.hovering = id;
        },
        unhoverRow(id) {
            if (this.hovering == id) {
                this.hovering = null;
            }
        },
        combinedColumnClass(item, column, index) {
            //add in item.class and column.class if they exist...

            let classes = [];
            if (item.class) {
                classes.push(item.class);
            }
            if (column.class) {
                classes.push(column.class);
            }

            if (!this.selectable) {
                classes.push('pl-4');
            } else {
                if (index != 0) {
                    classes.push('pl-4');
                }
            }

            let id = item.tra_id ? item.tra_id : item.id;
            if (id && this.hovering == id) {
                classes.push('bg-gray-100 cursor-pointer');
            }

            return classes;
        },
        updateUrlParams() {
            // Create an instance of URLSearchParams
            let params = new URLSearchParams();

            // Loop through the combinedUrlParams object and append each item to params
            for (let key in this.combinedUrlParams) {
                const value = this.combinedUrlParams[key];

                // If the value is an array, append each value under the same key
                if (Array.isArray(value)) {
                    value.forEach(item => params.append(key + '[]', item));
                } else {
                    // For single values, just append them directly
                    params.append(key, value);
                }
            }

            // Now, params.toString() will serialize your parameters into a query string
            // Update the URL without changing the browser history
            window.history.pushState({}, '', '?' + params.toString());
        },
        toggleSort(column) {          
            if (!column.sortable) return;
            if (this.orderBy == column.name) {
                this.orderByDir = this.orderByDir == 'asc' ? 'desc' : 'asc';
            } else {
                this.orderBy = column.name;
                this.orderByDir = column.defaultSortDir ? column.defaultSortDir : 'asc';
            }
            this.fetchPage = 1;
            this.refresh({clearOrderBy:false});
        },
        refresh(opts) {
            this.debouncedRefresh(opts);
        },
        debouncedRefresh: _.debounce(function(opts) {
            this.clearData(opts);
            this.fetchData();
        }, 300)
    },
    watch: {
        url: {
            handler: function (val, oldVal) {
                this.storeURL = val;
            },
            immediate: true
        },
        search: function (val) {
            if (this.initializing) return;

            this.debouncedSearch();
        },
        activeFilters: {
            handler: function (val) {
                this.refresh()
            },
            deep: true
        },
        combinedUrlParams: {
            handler: function (val) {
                this.updateUrlParams();
            },
            deep: true
        },

    },

}
</script>

<style>

.grid-container {
    display: grid;
    grid-gap: 0px;
}


.grid-container .selectable-input {
    padding-left: 1rem;
    width: 100%;
    height: 100%;   

}

.column-header {
    font-size: 12px;
    height: 51px;
}

.row {
    font-size: 15px;
    min-height: 49px;
}

.loading-overlay {
    background-color: rgba(255, 255, 255, 0.5);
    backdrop-filter: blur(4px);
    height: calc(100% - 51px);

}


</style>